// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * prepareEntityDescriptionDoc
 *
 * Write-time hook that parses `@mentions` from a task / milestone / project
 * `description` field and populates `mentions[]` with the hybrid
 * identifier+teamMemberId shape (and optional `userId` for human users).
 *
 * Sibling to `prepareDiscussionDoc` — same parser, same resolver, just
 * keyed off `description` instead of `content`. Lives as its own helper
 * so the d1-worker can fan out write hooks per collection without
 * branching inside `prepareDiscussionDoc`.
 *
 * Idempotent: re-running on a document that already has `mentions[]`
 * re-parses the description (covers description edits where the mention
 * list may have changed).
 *
 * Forward-only: existing rows are not migrated. Only writes that pass
 * through this hook get their mentions populated.
 */

import type { AgentLookupEntry } from './mentionResolver'
import { parseMentions } from './mentionParser'
import { resolveMentions } from './mentionResolver'
import type { DiscussionMention } from '../types'

/**
 * Mutate `doc.mentions` in place based on `doc.description`.
 *
 * - Empty / missing description → `mentions` left untouched (no key added).
 * - Non-empty description → `mentions` is set to the resolved array
 *   (replacing any prior value).
 *
 * Returns the same doc reference for ergonomic chaining in the caller's loop.
 */
export function prepareEntityDescriptionDoc<
  D extends {
    description?: unknown
    mentions?: DiscussionMention[]
  },
>(doc: D, lookup: Map<string, AgentLookupEntry>): D {
  if (typeof doc.description !== 'string' || doc.description.length === 0) {
    return doc
  }

  const parsed = parseMentions(doc.description)
  doc.mentions = resolveMentions(parsed, lookup)
  return doc
}
